import {
  Chip,
  CheckboxProps as HeroUICheckboxProps,
  tv,
  useCheckbox,
  VisuallyHidden,
} from "@heroui/react";
import { ReactNode } from "react";

export type FilterCheckboxProps = Omit<HeroUICheckboxProps, "children"> & {
  avatar?: ReactNode;
  label: string;
  ref?: React.Ref<HTMLInputElement>;
};

const checkbox = tv({
  compoundVariants: [
    {
      class: "border-primary ring-0",
      isHovered: true,
      isSelected: true,
    },
    {
      class: "border-gray-800 bg-white ring-0 forced-colors:border-[Mark]",
      isHovered: true,
      isInvalid: true,
    },
    {
      class: "h-8 border-primary forced-colors:bg-Highlight",
      isDisabled: true,
      isSelected: true,
    },
    {
      class: "h-8 border-danger forced-colors:border-[Mark]",
      isDisabled: true,
      isInvalid: true,
      isSelected: true,
    },
    {
      class: "h-[36px] border-transparent ring-2 ring-inset ring-primary",
      isFocusVisible: true,
      isSelected: true,
    },
  ],
  slots: {
    avatar: "text-gray-800 h-4 w-4 ml-1",
    base: "border-2 border-transparent text-center bg-white h-[36px] min-w-16 cursor-pointer px-1 rounded-lg ring-1 ring-inset ring-gray-500 forced-colors:border-[ButtonBorder] forced-colors:bg-[Canvas] forced-colors:ring-[ButtonBorder]",
    content:
      "text-gray-800 font-medium font-sans text-sm forced-colors:text-[ButtonText] ",
  },
  variants: {
    isDisabled: {
      true: {
        base: "pointer-events-none border-transparent ring-1 ring-inset ring-gray-500 forced-colors:ring-[GrayText] forced-colors:border-[GrayText]",
        content: "text-gray-600 forced-colors:text-[GrayText]",
      },
    },
    isFocusVisible: {
      true: {
        base: "outline outline-2 outline-offset-1 outline-primary forced-colors:ring-[Highlight]",
      },
    },
    isHovered: {
      true: {
        base: "border-gray-900 ring-0 h-8 forced-colors:border-[Highlight]",
      },
    },
    isInvalid: {
      true: {
        base: "border-danger bg-white ring-0 h-8 forced-colors:border-[Mark]",
      },
    },
    isSelected: {
      true: {
        base: "border-primary bg-blue-50 ring-0 h-8 forced-colors:border-[Highlight] forced-colors:bg-[Highlight]",
        content: "text-gray-950 forced-colors:text-[HighlightText]",
      },
    },
  },
});

export function FilterCheckbox({
  avatar,
  label,
  ref,
  ...rest
}: FilterCheckboxProps) {
  const {
    children,
    getBaseProps,
    getInputProps,
    isDisabled,
    isFocusVisible,
    isHovered,
    isInvalid,
    isSelected,
  } = useCheckbox({
    ref,
    ...rest,
  });

  const styles = checkbox({
    isDisabled,
    isFocusVisible,
    isHovered,
    isInvalid,
    isSelected,
  });

  return (
    <label {...getBaseProps()}>
      <VisuallyHidden>
        <input {...getInputProps()} className="outline-none" />
      </VisuallyHidden>
      <Chip
        avatar={avatar}
        classNames={{
          avatar: styles.avatar(),
          base: styles.base(),
          content: styles.content(),
        }}
      >
        {children ?? label}
      </Chip>
    </label>
  );
}

FilterCheckbox.displayName = "RecareUI.FilterCheckbox";
